import { useEffect, useState } from 'react';
import { useAppDispatch } from '../hooks/redux';
import { fetchConfiguration } from '../features/configuration/thunk';
import { fetchUserData } from '@common/features/user/thunks';
import { AuthContextProps, AuthProviderProps, hasAuthParams, useAuth } from 'react-oidc-context';
import { WebStorageStateStore, User } from 'oidc-client-ts';
import { sendDataLayerCustomEvent } from '@common/services/dataLayerService';
import { setUserInfo } from '@common/features/user/userSlice';
import { getAuthConfig } from '@common/hooks/useGetAuthConfig';

const isProdEnv = process.env.REACT_APP_ENV === 'prod';

const oidcConfig: AuthProviderProps = {
  redirect_uri: `${process.env.REACT_APP_PUBLIC_URL}/`,
  post_logout_redirect_uri: `${process.env.REACT_APP_PUBLIC_URL}/`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback: () => {
    sendDataLayerCustomEvent('successful_login');
    redirectToIntendedUrl();
  },
  automaticSilentRenew: true,
  monitorSession: isProdEnv,
};

const useOidcAuthentication = () => {
  const auth = useAuth();
  useLogoutOnAllTabs(auth);
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    handleLoginCancelation(auth);

    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      saveIntendedUrlToStorage();
      auth.signinRedirect();
      setHasTriedSignin(true);
    }

    if (auth.isAuthenticated && !hasTriedSignin) {
      dispatch(fetchUserData({}));

      const user = getUser();
      dispatch(
        setUserInfo({
          user_name: user?.profile.name,
          user_id: user?.profile.extension_parties_id,
          email: (user?.profile.emails as Array<string>)[0],
        }),
      );

      dispatch(fetchConfiguration({}));
    }

    auth?.events.addSilentRenewError((e) => {
      handleSilentRenewError(e, auth);
    });
  }, [auth, dispatch, hasTriedSignin]);

  return {
    isAuthenticated: auth.isAuthenticated,
    logoutUser: () => {
      logoutUser(auth);
    },
  };
};

const handleSilentRefreshBadRequest = (auth: AuthContextProps) => {
  setTimeout(() => {
    const accessToken = getUser()?.access_token;
    if (accessToken) {
      const now = Math.floor(Date.now() / 1000);
      var expiry = getUser()?.expires_at;
      if (expiry && expiry > now) {
        auth.signinSilent();
      } else {
        logoutUser(auth);
      }
    } else {
      logoutUser(auth);
    }
  }, 5000);
};

const handleSilentRenewError = (error: any, auth: AuthContextProps) => {
  if (error.message.includes('invalid_grant')) {
    handleSilentRefreshBadRequest(auth);
  }
};

const handleLoginCancelation = (auth: AuthContextProps) => {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (!error) {
    return;
  }

  const description = (params.get('error_description') || '').toUpperCase();
  const hasUserCancelled = description.includes('AADB2C90091');
  if (hasUserCancelled) {
    window.history.replaceState({}, document.title, window.location.pathname);
    auth.signinRedirect();
  }
};

const useLogoutOnAllTabs = (auth: AuthContextProps) => {
  useEffect(() => {
    const syncLogout = (event: any) => {
      if (event.key === 'logout') {
        auth.signoutRedirect();
      }
    };
    window.addEventListener('storage', syncLogout);
    return () => {
      window.removeEventListener('storage', syncLogout);
    };
  }, [auth]);
};

const saveIntendedUrlToStorage = () => {
  const currentUrl = window.location.pathname + window.location.search;
  sessionStorage.setItem('redirectUrl', currentUrl);
};

const redirectToIntendedUrl = () => {
  const redirectUrl = sessionStorage.getItem('redirectUrl') || '/';
  sessionStorage.removeItem('redirectUrl');
  window.location.href = redirectUrl;
};

const getUser = () => {
  const authConfig = getAuthConfig();
  if (!authConfig) {
    return null;
  }

  const oidcStorage = localStorage.getItem(`oidc.user:${authConfig.authority}:${authConfig.client_id}`);
  if (oidcStorage === null) {
    return null;
  }

  return User.fromStorageString(oidcStorage);
};

const useLogoutUser = () => {
  const auth = useAuth();

  return { logoutUser: () => logoutUser(auth) };
};

const logoutUser = (auth: AuthContextProps) => {
  if (!isProdEnv) {
    localStorage.setItem('logout', Date.now().toString());
  }
  auth.signoutRedirect();
};

export { useOidcAuthentication, oidcConfig, getUser, useLogoutUser };
